import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft,
  Upload,
  Download,
  Globe,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Loader2,
  Code,
  Copy,
  Wifi,
  WifiOff,
  CheckCircle2,
  PauseCircle,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { websocketService } from '@/services/socket.io';
import type {
  ScrapingRequest,
  ScrapingCompletedData,
} from '@/services/socket.io';
import { categoriesAPI } from '@/services/categories';
import type { Category, SubCategory } from '@/services/categories';

interface ImportResult {
  success: number;
  failed: number;
  total: number;
  errors: Array<{
    row: number;
    field: string;
    message: string;
  }>;
}

interface PreviewProduct {
  name: string;
  price: string;
  category: string;
  description: string;
  image: string;
  product_url?: string;
  image_urls?: string[];
  discount?: string;
  color?: string;
  width?: string;
  height?: string;
  length?: string;
}

// Helper function to get status color
const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
      return '#10b981'; // green-500
    case 'failed':
      return '#ef4444'; // red-500
    case 'in_progress':
      return '#3b82f6'; // blue-500
    case 'stopped':
      return '#f59e0b'; // amber-500
    default:
      return '#6b7280'; // gray-500
  }
};

// Helper function to get status message
const getStatusMessage = (status: string) => {
  switch (status) {
    case 'pending':
      return 'Kutish rejimida...';
    case 'in_progress':
      return 'Yuklanmoqda...';
    case 'completed':
      return 'Muvaffaqiyatli yakunlandi';
    case 'failed':
      return 'Xatolik yuz berdi';
    case 'stopped':
      return "To'xtatilgan";
    default:
      return 'Jarayon davom etmoqda...';
  }
};

export function BulkProductAdd() {
  const navigate = useNavigate();
  const handleBack = () => navigate(-1);
  const [activeTab, setActiveTab] = useState('file-upload');

  // WebSocket connection state
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<
    'connecting' | 'connected' | 'disconnected'
  >('disconnected');

  // Categories state
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('');
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);

  // File upload state
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState({
    excel: 0,
    zip: 0,
  });
  const [uploadStatus, setUploadStatus] = useState<
    'idle' | 'uploading' | 'processing' | 'completed' | 'error'
  >('idle');
  const [uploadResult, setUploadResult] = useState<ImportResult | null>(null);
  const [currentUpload, setCurrentUpload] = useState<'excel' | 'zip' | null>(
    null
  );

  // Format time in HH:MM:SS or MM:SS format
  const formatDuration = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs
        .toString()
        .padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Initialize progress state
  const [importProgress, setImportProgress] = useState<{
    processed: number;
    total: number;
    status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'stopped';
    message: string;
    startTime?: Date;
    endTime?: Date;
    speed?: number; // items per second
    elapsedTime?: number; // in seconds
  }>({
    processed: 0,
    total: 1, // Default to 1 to avoid division by zero
    status: 'pending',
    message: '',
    startTime: undefined,
    endTime: undefined,
    speed: 0,
    elapsedTime: 0,
  });

  // Update elapsed time every second when upload is in progress
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (importProgress.status === 'in_progress' && importProgress.startTime) {
      interval = setInterval(() => {
        const now = new Date();
        const elapsed = Math.floor(
          (now.getTime() - new Date(importProgress.startTime!).getTime()) / 1000
        );

        setImportProgress((prev) => ({
          ...prev,
          elapsedTime: elapsed,
        }));
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [importProgress.status, importProgress.startTime]);

  // Web scraping state
  const [scrapingUrl, setScrapingUrl] = useState('');
  const [puppeteerScript, setPuppeteerScript] = useState('');
  const [scrapingProgress, setScrapingProgress] = useState(0);
  const [scrapingStatus, setScrapingStatus] = useState<
    'idle' | 'scraping' | 'processing' | 'completed' | 'error'
  >('idle');

  const [scrapingStats, setScrapingStats] = useState({
    processed: 0,
    total: 1,
    status: 'idle',
    message: '',
    startTime: undefined as Date | undefined,
    endTime: undefined as Date | undefined,
    speed: 0,
    elapsedTime: 0,
  });
  const [previewProducts, setPreviewProducts] = useState<PreviewProduct[]>([]);
  const [scrapingResult, setScrapingResult] = useState<ImportResult | null>(
    null
  );
  const [_currentJobId, setCurrentJobId] = useState<string | null>(null);

  // Load categories on component mount
  useEffect(() => {
    loadCategories();
    connectWebSocket();

    return () => {
      websocketService.disconnect();
    };
  }, []);

  // Handle job progress updates
  const handleJobProgress = (data: any) => {
    console.log('Job progress update:', data);

    // Handle different data formats from the server
    const progressData = {
      status: data.status || 'in_progress',
      processed: data.completedCount ?? 0,
      total: data.totalCount ?? 0,
      message: data.message || 'Processing...',
      startTime: data.startTime ? new Date(data.startTime) : new Date(),
      endTime: data.endTime ? new Date(data.endTime) : undefined,
      speed: data.speed ?? 0,
      elapsedTime: 0,
    };

    // Calculate elapsed time if start time is available
    if (progressData.startTime) {
      const now = new Date();
      progressData.elapsedTime = Math.floor(
        (now.getTime() - progressData.startTime.getTime()) / 1000
      );
    }

    console.log('Processed progress data:', progressData);

    setImportProgress((prev) => {
      // Calculate speed (items per second)
      const now = new Date();
      const prevTime = prev.startTime
        ? new Date(prev.startTime).getTime()
        : now.getTime();
      const timeElapsed = (now.getTime() - prevTime) / 1000; // in seconds
      const itemsProcessed = progressData.processed - (prev.processed || 0);
      const speed =
        timeElapsed > 0
          ? Math.max(0, Math.round((itemsProcessed / timeElapsed) * 10) / 10)
          : 0;

      return {
        ...prev,
        processed: progressData.processed,
        total: progressData.total > 0 ? progressData.total : prev.total || 1, // Ensure at least 1 to avoid division by zero
        status: progressData.status,
        message: progressData.message,
        startTime: prev.startTime || now,
        endTime: ['completed', 'failed', 'stopped'].includes(
          progressData.status
        )
          ? now
          : prev.endTime,
        speed:
          progressData.status === 'in_progress'
            ? progressData.speed || speed
            : 0,
      };
    });

    // If job is completed or failed, update the upload status
    if (['completed', 'failed', 'stopped'].includes(progressData.status)) {
      setUploadStatus(
        progressData.status === 'completed' ? 'completed' : 'error'
      );

      // Show completion toast
      if (progressData.status === 'completed') {
        toast.success(progressData.message || 'Import completed successfully');
      } else if (progressData.status === 'failed') {
        toast.error(progressData.message || 'Import failed');
      }
    }
  };

  // WebSocket connection management
  const connectWebSocket = async () => {
    try {
      setConnectionStatus('connecting');
      await websocketService.connect();
      setIsConnected(true);
      setConnectionStatus('connected');
      toast.success('WebSocket connected successfully');

      // Set up WebSocket listeners
      websocketService.on('jobProgress', handleJobProgress);

      // Handle scraping progress events
      const handleScrapingProgressEvent = (data: any) => {
        console.log(' Scraping progress event:', data);
        const status = data.status || 'in_progress';
        handleScrapingProgress({
          ...data,
          status: status,
          processed: data.processed || data.completedCount || 0,
          total: data.total || data.totalCount || 1,
          message:
            data.message ||
            (status === 'completed'
              ? 'Scraping completed'
              : 'Scraping in progress...'),
        });

        // If status is completed, also call handleScrapingCompleted
        if (status === 'completed') {
          handleScrapingCompleted(data);
        }
      };

      // Handle scraping completion
      const handleScrapingCompletedEvent = (data: any) => {
        console.log(' Scraping completed event:', data);
        // Update the data to ensure it has the completed status
        const completedData = {
          ...data,
          status: 'completed',
          processed: data.processed || data.total || 1,
          total: data.total || 1,
          message: data.message || 'Scraping completed successfully',
          endTime: new Date(),
        };

        // First update the progress to show completion
        handleScrapingProgress(completedData);

        // Then handle the completion logic
        handleScrapingCompleted(completedData);
      };

      // Handle scraping errors
      const handleScrapingErrorEvent = (error: any) => {
        console.error(' Scraping error event:', error);
        handleScrapingProgress({
          ...error,
          status: 'failed',
          message: error.message || 'Scraping failed',
          endTime: new Date(),
        });
      };

      // Register event listeners
      websocketService.on('scraping-progress', handleScrapingProgressEvent);
      websocketService.on('scraping-completed', handleScrapingCompletedEvent);
      websocketService.on('scraping-error', handleScrapingErrorEvent);

      // Also listen to jobProgress for scraping updates (as fallback)
      websocketService.on('jobProgress', (data: any) => {
        console.log('📡 [jobProgress] Event received:', data.type);

        if (data.type === 'web_scraping' || data.jobType === 'web_scraping') {
          console.log('🔍 [jobProgress] Processing as scraping job:', data);
          handleScrapingProgressEvent({
            ...data,
            completedCount: data.completedCount ?? data.processed,
            totalCount: data.totalCount ?? data.total,
          });
        } else {
          console.log('ℹ️ [jobProgress] Not a scraping job, ignoring');
        }
      });

      return () => {
        // Clean up event listeners
        websocketService.off('scraping-progress', handleScrapingProgressEvent);
        websocketService.off(
          'scraping-completed',
          handleScrapingCompletedEvent
        );
        websocketService.off('scraping-error', handleScrapingErrorEvent);
        websocketService.disconnect();
      };
    } catch (error) {
      console.error('WebSocket connection failed:', error);
      setIsConnected(false);
      setConnectionStatus('disconnected');
      toast.error('Failed to connect to WebSocket');
    }
  };

  // Load categories
  const loadCategories = async () => {
    try {
      console.log(' Loading categories...');
      const response = await categoriesAPI.getCategories({ limit: 100 });
      setCategories(response.data);
      console.log(' Categories loaded:', response.data.length);
    } catch (error) {
      console.error(' Failed to load categories:', error);
      toast.error('Failed to load categories');
    }
  };

  // WebSocket event handlers - updated with proper types
  const handleScrapingProgress = (data: any) => {
    console.log(
      '🔍 [handleScrapingProgress] Raw data received:',
      JSON.stringify(data, null, 2)
    );

    const now = new Date();
    // Create progress data with fallbacks
    const processed = data.completedCount ?? data.processed ?? 0;
    const total = data.totalCount ?? data.total ?? 1; // Default to 1 to avoid division by zero

    const progressData = {
      status: data.status,
      processed: processed,
      total: total,
      message: data.message || 'Scraping in progress...',
      startTime: data.startTime
        ? new Date(data.startTime)
        : scrapingStats?.startTime || now,
      endTime: data.endTime ? new Date(data.endTime) : undefined,
      speed: data.speed ?? 0,
      elapsedTime: 0,
    };

    // Calculate elapsed time if start time is available
    if (progressData.startTime) {
      progressData.elapsedTime = Math.floor(
        (now.getTime() - new Date(progressData.startTime).getTime()) / 1000
      );
    }

    console.log('📈 Processing progress data:', {
      processed,
      total,
      progress:
        total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0,
      status: progressData.status,
      message: progressData.message,
    });

    // Update progress bar
    const newProgress =
      progressData.total > 0
        ? Math.min(
            100,
            Math.max(0, (progressData.processed / progressData.total) * 100)
          )
        : 0;

    console.log('📊 Setting scraping progress to:', newProgress);
    setScrapingProgress(newProgress);

    // Update scraping stats (single update)
    setScrapingStats((prev) => {
      // Calculate speed (items per second)
      const prevTime = prev.startTime
        ? new Date(prev.startTime).getTime()
        : now.getTime();
      const timeElapsed = (now.getTime() - prevTime) / 1000;
      const itemsProcessed = progressData.processed - (prev.processed || 0);
      const speed =
        timeElapsed > 0
          ? Math.max(0, Math.round((itemsProcessed / timeElapsed) * 10) / 10)
          : 0;

      return {
        ...prev,
        processed: progressData.processed,
        total: progressData.total,
        status: progressData.status,
        message: progressData.message,
        startTime: progressData.startTime,
        endTime: progressData.endTime,
        speed: ['completed', 'failed', 'stopped'].includes(progressData.status)
          ? 0
          : progressData.speed || speed,
        elapsedTime: progressData.elapsedTime,
      };
    });

    // Update scraping status based on progress (single status check)
    if (['completed', 'failed', 'stopped'].includes(progressData.status)) {
      const finalStatus =
        progressData.status === 'completed' ? 'completed' : 'error';
      console.log('✅ Scraping status changed to:', finalStatus);
      setScrapingStatus(finalStatus);

      // Show toast notification for completion/failure
      if (progressData.status === 'completed') {
        toast.success(
          progressData.message || 'Scraping completed successfully!'
        );
      } else {
        toast.error(progressData.message || 'Scraping failed');
      }
    } else {
      console.log('🔄 Scraping in progress...');
      setScrapingStatus('scraping');
    }
  };

  const handleScrapingCompleted = (data: ScrapingCompletedData) => {
    console.log('✅ [handleScrapingCompleted] Scraping completed:', data);
    if (data.status === 'completed') {
      // Update progress to 100%
      setScrapingProgress(100);

      // Update scraping stats
      setScrapingStats((prev) => ({
        ...prev,
        status: 'completed',
        processed: prev.total || 1,
        total: prev.total || 1,
        message: data.message || 'Scraping completed',
        endTime: new Date(),
        speed: 0,
      }));

      // Set scraping status to completed
      setScrapingStatus('completed');

      if (data.status === 'completed') {
        // Type assertion for the data
        const products = data.data as PreviewProduct[];
        console.log(`✅ Found ${products.length} products`);

        setPreviewProducts(products);

        const result: ImportResult = {
          success: products.length,
          failed: 0,
          total: products.length,
          errors: [],
        };

        setScrapingResult(result);
        toast.success(`Scraping completed! ${products.length} products found.`);
      } else {
        console.error('❌ Scraping completed but no data received');
        setScrapingStatus('error');
        toast.error(data.message || 'Scraping failed - no data received');
      }
    } else {
      console.log("ℹ️ Ignoring completed job - job ID doesn't match");
    }
  };

  // const handleScrapingError = (data: ScrapingErrorData) => {
  //   console.error(" Scraping error:", data);
  //   if (data.jobId === currentJobId) {
  //     setScrapingStatus("error");
  //     toast.error(data.message || "Scraping failed");
  //   }
  // };

  // Handle file input change and upload immediately
  const handleFileChange = async (file: File | null, type: 'excel' | 'zip') => {
    if (!file) {
      if (type === 'excel') {
        setCsvFile(null);
      } else {
        setZipFile(null);
      }
      return;
    }

    try {
      setIsUploading(true);

      // Just set the file, we'll handle the upload when the user clicks Import
      if (type === 'excel') {
        setCsvFile(file);
        toast.success('Excel fayli tanlandi');
      } else {
        setZipFile(file);
        toast.success('ZIP fayli tanlandi');
      }
    } catch (error) {
      console.error(` Fayl yuklanmadi:`, error);
      toast.error(`${type === 'excel' ? 'Excel' : 'ZIP'} fayli yuklanmadi`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!csvFile) {
      toast.error('Iltimos, Excel faylni yuklang');
      return;
    }

    if (!zipFile) {
      toast.error('Iltimos, rasm fayllari bilan ZIP faylni ham yuklang');
      return;
    }

    try {
      setUploadStatus('uploading');
      setUploadProgress({ excel: 0, zip: 0 });

      const response = await websocketService.importProducts(
        csvFile,
        zipFile,
        (type, progress) => {
          setCurrentUpload(type);
          setUploadProgress((prev) => ({
            ...prev,
            [type]: progress,
          }));
        }
      );

      console.log(' Import started successfully:', response);
      setUploadStatus('processing');
      toast.success('Import boshlandi! Jarayon orqa fonda davom etmoqda.');
    } catch (error) {
      console.error(' Import failed:', error);
      setUploadStatus('error');
      toast.error(
        error instanceof Error
          ? error.message
          : "Import amalga oshirilmadi. Iltimos, qayta urinib ko'ring."
      );
    }
  };

  const handleWebScraping = async () => {
    console.log(' Starting web scraping...');
    if (!scrapingUrl) {
      console.log(' No scraping URL provided');
      toast.error('Please enter a website URL');
      return;
    }

    if (!selectedCategory) {
      console.log(' No category selected');
      toast.error('Please select a category');
      return;
    }

    if (!isConnected) {
      console.log(' WebSocket not connected');
      toast.error('WebSocket connection is required for scraping');
      return;
    }

    console.log('Scraping URL:', scrapingUrl);
    console.log('Selected category:', selectedCategory);
    console.log('Selected subcategory:', selectedSubCategory);

    setScrapingStatus('scraping');
    setScrapingProgress(0);

    try {
      const scrapingRequest: ScrapingRequest = {
        url: scrapingUrl,
        code: puppeteerScript || getDefaultScrapingCode(),
        categoryId: parseInt(selectedCategory),
        subCategoryId: selectedSubCategory
          ? parseInt(selectedSubCategory)
          : undefined,
      };

      console.log('Sending scraping request:', scrapingRequest);

      const response = await websocketService.startScraping(scrapingRequest);
      console.log(' Scraping response:', response);

      if (response.success && response.jobId) {
        setCurrentJobId(response.jobId);
        setScrapingStatus('processing');
        toast.success('Scraping job started successfully');
      } else {
        setScrapingStatus('error');
        toast.error(response.message || 'Failed to start scraping');
      }
    } catch (error) {
      console.error(' Scraping failed:', error);
      setScrapingStatus('error');
      toast.error('Scraping failed. Please try again.');
    }
  };

  const getDefaultScrapingCode = () => {
    return `const scrape = async (url) => {
  const page = await browser.newPage();
  try {
    console.log(\`Sahifaga o'tilmoqda: \${url}\`);
    await page.goto(url, { waitUntil: "networkidle2" });
    const data = await page.evaluate(() => {
      const titleElement = document.querySelector("h1.breadcrumbs__title");
      const mainImagesSelector = ".sku__vertical-slides .swiper-slide img";
      const imgElements = document.querySelectorAll(mainImagesSelector);
      const urls = Array.from(imgElements).map(
        (img) => img.getAttribute("data-zoom-image") || img.src
      );
      let currentPrice = null;
      let oldPrice = null;
      const descContainer = document.querySelector(".sku__desc");
      const priceElement = descContainer.querySelector("p.sku__price");

      if (priceElement) {
        const oldPriceElement = priceElement.querySelector("del");
        if (oldPriceElement) {
          oldPrice = parseFloat(
            oldPriceElement.textContent.trim().replace(/[^\\d.]/g, "")
          );
          oldPriceElement.remove();
        }
        const insElements = priceElement.querySelectorAll("ins");
        const insPriceElements =
          priceElement.querySelectorAll("ins.sku__price-ins");
        if (insPriceElements.length > 0) {
          currentPrice = parseFloat(
            insPriceElements[0].textContent
              .trim()
              .replace(/[^\\d.]/g, "")
              .toString()
          );
        }
        insElements.forEach((el) => el.remove());
        const spanElements = priceElement.querySelectorAll("span, ins");
        spanElements.forEach((el) => el.remove());
        const priceText = priceElement.textContent.trim();
        if (priceText) {
          currentPrice = parseFloat(
            priceText.replace(/[^\\d.]/g, "")
          ).toString();
        }
      }
      const descriptionElement = document.querySelector(
        ".details__txt-readmore .editor"
      );
      const description = descriptionElement
        ? descriptionElement.innerText.trim()
        : "";
      let color = null;
      let productType = null;
      let width = null;
      let length = null;
      let height = null;
      const rows = document.querySelectorAll(".sku__details-table tbody tr");
      rows.forEach((row) => {
        const cells = row.querySelectorAll("td");
        cells.forEach((cell) => {
          const key = cell.querySelector("small")?.innerText.trim();
          const value = cell.innerText.replace(key, "").trim();
          if (key === "Цвет") {
            color = value;
          } else if (key === "Вид товара") {
            productType = value;
          } else if (key === "Ширина") {
            width = value.replace(/\\D/g, "");
          } else if (key === "Длина") {
            length = value.replace(/\\D/g, "");
          } else if (key === "Высота") {
            height = value.replace(/\\D/g, "");
          }
        });
      });
      return {
        name: titleElement ? titleElement.innerText.trim() : "",
        price: currentPrice.toString(),
        product_url: window.location.href,
        image_urls: urls,
        discount: oldPrice
          ? String(parseInt(oldPrice, 10) - parseInt(currentPrice || "0", 10))
          : undefined,
        description: description || undefined,
        color: color || undefined,
        width: width || undefined,
        height: height || undefined,
        length: length || undefined,
      };
    });
    console.log("Ma'lumotlar muvaffaqiyatli olindi:");
    console.log(data);
    return { success: true, data };
  } catch (error) {
    if (error.browser) error.browser.close();
    return { success: false, message: "Scraping not completed" };
  }
};`;
  };

  const downloadTemplate = () => {
    console.log(' Downloading template...');
    // Mock download template
    const csvContent = `Product Name,Category,Price,Description,SKU,Stock,Image Filename
Modern Chair,Furniture,129.99,Comfortable modern dining chair,MC-001,25,chair-001.jpg
Ceramic Vase,Decor,45.99,Decorative ceramic vase,CV-002,15,vase-002.jpg`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = 'product-template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    console.log(' Template downloaded successfully');
    toast.success('Template downloaded successfully');
  };

  const resetUpload = () => {
    setCsvFile(null);
    setZipFile(null);
    setUploadProgress({ excel: 0, zip: 0 });
    setUploadStatus('idle');
    setUploadResult(null);
  };

  const resetScraping = () => {
    setScrapingUrl('');
    setPuppeteerScript('');
    setScrapingProgress(0);
    setScrapingStatus('idle');
    setScrapingStats({
      processed: 0,
      total: 1,
      status: 'idle',
      message: '',
      startTime: undefined,
      endTime: undefined,
      speed: 0,
      elapsedTime: 0,
    });
    setScrapingResult(null);
    setPreviewProducts([]);
    setCurrentJobId(null);
    setSelectedCategory('');
    setSelectedSubCategory('');
  };

  // Handle category selection
  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setSelectedSubCategory('');

    const category = categories.find((cat) => cat.id.toString() === categoryId);
    if (category) {
      setSubCategories(category.subCategories || []);
    } else {
      setSubCategories([]);
    }
  };

  // Puppeteer script management
  const loadExampleScript = (scriptType: string) => {
    const examples = {
      basic: `// Basic product scraping
await page.goto(url);
await page.waitForSelector('.product-item');

const products = await page.evaluate(() => {
  return Array.from(document.querySelectorAll('.product-item')).map(item => ({
    name: item.querySelector('.product-title')?.textContent?.trim() || '',
    price: item.querySelector('.price')?.textContent?.trim() || '',
    description: item.querySelector('.product-desc')?.textContent?.trim() || '',
    image: item.querySelector('img')?.src || '',
    url: item.querySelector('a')?.href || ''
  }));
});

return products;`,
      pagination: `// Handle pagination
let allProducts = [];
let currentPage = 1;
let hasNextPage = true;

while (hasNextPage && currentPage <= 10) {
  await page.goto(\`\${url}?page=\${currentPage}\`);
  await page.waitForSelector('.product-item');
  
  const products = await page.evaluate(() => {
    return Array.from(document.querySelectorAll('.product-item')).map(item => ({
      name: item.querySelector('.product-title')?.textContent?.trim() || '',
      price: item.querySelector('.price')?.textContent?.trim() || '',
      image: item.querySelector('img')?.src || ''
    }));
  });
  
  allProducts.push(...products);
  
  // Check if next page exists
  hasNextPage = await page.$('.next-page') !== null;
  currentPage++;
}

return allProducts;`,
      dynamic: `// Handle dynamic content with waiting
await page.goto(url);

// Wait for dynamic content to load
await page.waitForFunction(() => {
  const products = document.querySelectorAll('.product-item');
  return products.length > 0;
}, { timeout: 10000 });

// Scroll to load more content if needed
await page.evaluate(() => {
  window.scrollTo(0, document.body.scrollHeight);
});

await page.waitForTimeout(2000);

const products = await page.evaluate(() => {
  return Array.from(document.querySelectorAll('.product-item')).map(item => ({
    name: item.querySelector('h2, .title, .name')?.textContent?.trim() || '',
    price: item.querySelector('.price, .cost')?.textContent?.replace(/[^0-9.,]/g, '') || '',
    description: item.querySelector('.desc, .description')?.textContent?.trim() || '',
    image: item.querySelector('img')?.src || ''
  }));
});

return products;`,
    };

    setPuppeteerScript(
      examples[scriptType as keyof typeof examples] || examples.basic
    );
  };

  const copyScriptToClipboard = () => {
    navigator.clipboard.writeText(puppeteerScript);
    toast.success('Script copied to clipboard');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between md:items-center flex-col md:flex-row gap-4">
        <div className="flex  items-center gap-2">
          <Button variant="ghost" size="sm" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden md:block">Back to Products</span>
          </Button>
          <div className="flex-1">
            <h1 className="leading-normal">Bulk Product Import</h1>
            <p className="text-muted-foreground hidden md:block">
              Import products in bulk using CSV/Excel files or web scraping
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isConnected ? (
            <Badge variant="default" className="bg-green-100 text-green-800">
              <Wifi className="h-3 w-3 mr-1" />
              Connected
            </Badge>
          ) : (
            <Badge variant="destructive" className="bg-red-100 text-red-800">
              <WifiOff className="h-3 w-3 mr-1" />
              Disconnected
            </Badge>
          )}
          <Button
            variant="outline"
            size="sm"
            // onClick={connectWebSocket}
            disabled={connectionStatus === 'connecting'}
          >
            {connectionStatus === 'connecting' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Wifi className="h-4 w-4" />
            )}
            {connectionStatus === 'connecting' ? 'Connecting...' : 'Reconnect'}
          </Button>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-6"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="file-upload" className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            File Upload
          </TabsTrigger>
          <TabsTrigger value="web-scraping" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Web Scraping
          </TabsTrigger>
        </TabsList>

        {/* File Upload Tab */}
        <TabsContent value="file-upload" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Upload Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Upload Files
                </CardTitle>
                <CardDescription>
                  Upload a CSV or Excel file with product data and an optional
                  ZIP file containing product images
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Compact Progress Bar */}
                {importProgress.status !== 'pending' && (
                  <div className="mb-6 p-3 bg-card border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {importProgress.status === 'in_progress' ? (
                          <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
                        ) : importProgress.status === 'completed' ? (
                          <CheckCircle2 className="h-3 w-3 text-green-500" />
                        ) : (
                          <XCircle className="h-3 w-3 text-red-500" />
                        )}
                        <span className="text-sm font-medium">
                          {importProgress.status === 'in_progress'
                            ? 'Importing...'
                            : importProgress.status === 'completed'
                              ? 'Completed'
                              : importProgress.status === 'failed'
                                ? 'Failed'
                                : 'Stopped'}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {importProgress.processed} / {importProgress.total}
                        </span>
                      </div>
                      <span className="text-sm font-medium">
                        {importProgress.total > 0
                          ? `${Math.round(
                              (importProgress.processed /
                                importProgress.total) *
                                100
                            )}%`
                          : '0%'}
                      </span>
                    </div>
                    <Progress
                      value={
                        importProgress.total > 0
                          ? (importProgress.processed / importProgress.total) *
                            100
                          : 0
                      }
                      className="h-1.5 mb-1"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <div>
                        {importProgress.speed !== undefined &&
                          importProgress.speed > 0 && (
                            <span>{importProgress.speed} items/sec • </span>
                          )}
                        {importProgress.status === 'in_progress' ? (
                          <span>
                            Elapsed:{' '}
                            {formatDuration(importProgress.elapsedTime || 0)}
                          </span>
                        ) : importProgress.endTime &&
                          importProgress.startTime ? (
                          <span>
                            Duration:{' '}
                            {formatDuration(
                              Math.round(
                                (importProgress.endTime.getTime() -
                                  importProgress.startTime.getTime()) /
                                  1000
                              )
                            )}
                          </span>
                        ) : null}
                      </div>
                      {importProgress.startTime && (
                        <span>
                          {importProgress.status === 'in_progress'
                            ? `Started ${new Date(
                                importProgress.startTime
                              ).toLocaleTimeString()}`
                            : `Completed at ${new Date(
                                importProgress.endTime || new Date()
                              ).toLocaleTimeString()}`}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Product Data File (CSV/Excel)</Label>
                  <Input
                    id="csv-file"
                    type="file"
                    accept=".csv, .xlsx, .xls"
                    disabled={isUploading}
                    onChange={(e) =>
                      handleFileChange(
                        e.target.files ? e.target.files[0] : null,
                        'excel'
                      )
                    }
                  />
                  {csvFile && (
                    <p className="text-sm text-muted-foreground">
                      Selected: {csvFile.name} (
                      {(csvFile.size / 1024).toFixed(1)} KB)
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Product Images (ZIP file) - Optional</Label>
                  <Input
                    id="zip-file"
                    type="file"
                    accept=".zip"
                    disabled={isUploading}
                    onChange={(e) =>
                      handleFileChange(
                        e.target.files ? e.target.files[0] : null,
                        'zip'
                      )
                    }
                  />
                  {zipFile && (
                    <p className="text-sm text-muted-foreground">
                      Selected: {zipFile.name} (
                      {(zipFile.size / 1024 / 1024).toFixed(1)} MB)
                    </p>
                  )}
                </div>

                <Separator />

                <div className="flex gap-2">
                  <Button
                    onClick={handleFileUpload}
                    disabled={
                      !csvFile ||
                      !zipFile ||
                      uploadStatus === 'uploading' ||
                      uploadStatus === 'processing'
                    }
                    className="flex-1"
                  >
                    {uploadStatus === 'uploading' ||
                    uploadStatus === 'processing' ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        {uploadStatus === 'uploading'
                          ? 'Uploading...'
                          : 'Processing...'}
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Import Products
                      </>
                    )}
                  </Button>
                  <Button variant="outline" onClick={downloadTemplate}>
                    <Download className="h-4 w-4 mr-2" />
                    Template
                  </Button>
                </div>

                {(uploadStatus === 'uploading' ||
                  uploadStatus === 'processing') && (
                  <div className="space-y-4">
                    {/* Excel Upload Progress */}
                    {uploadStatus === 'processing' && (
                      <div className="mt-4 space-y-4 p-4 border rounded-lg bg-muted/20">
                        {/* Status Badge */}
                        <div className="flex items-center gap-2">
                          {importProgress.status === 'in_progress' && (
                            <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                          )}
                          {importProgress.status === 'completed' && (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          )}
                          {importProgress.status === 'failed' && (
                            <XCircle className="h-4 w-4 text-red-500" />
                          )}
                          {importProgress.status === 'stopped' && (
                            <PauseCircle className="h-4 w-4 text-amber-500" />
                          )}
                          <span className="text-sm font-medium capitalize">
                            {importProgress.status.replace('_', ' ')}
                            {importProgress.message &&
                              `: ${importProgress.message}`}
                          </span>
                        </div>

                        {/* Progress Bar */}
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">
                              Progress
                            </span>
                            <span className="font-medium">
                              {importProgress.processed} /{' '}
                              {importProgress.total}
                            </span>
                          </div>
                          <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary">
                            <div
                              className="h-full transition-all duration-300 ease-in-out"
                              style={{
                                width:
                                  importProgress.total > 0
                                    ? `${Math.min(
                                        100,
                                        Math.round(
                                          (importProgress.processed /
                                            importProgress.total) *
                                            100
                                        )
                                      )}%`
                                    : '0%',
                                backgroundColor: getStatusColor(
                                  importProgress.status
                                ),
                              }}
                            />
                          </div>
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>
                              {importProgress.total > 0
                                ? `${Math.min(
                                    100,
                                    Math.round(
                                      (importProgress.processed /
                                        importProgress.total) *
                                        100
                                    )
                                  )}%`
                                : '0%'}
                            </span>
                            <span>
                              {getStatusMessage(importProgress.status)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-sm font-medium">
                          {currentUpload === 'excel' ? '' : ''} Excel fayli
                        </span>
                        <span>{uploadProgress.excel}%</span>
                      </div>
                      <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary">
                        <div
                          className="h-full w-full flex-1 bg-primary transition-all"
                          style={{
                            width: `${uploadProgress.excel}%`,
                            backgroundColor:
                              currentUpload === 'excel' ? '#3b82f6' : '#10b981',
                          }}
                        />
                      </div>
                    </div>

                    {/* ZIP Upload Progress */}
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-sm font-medium">
                          {currentUpload === 'zip' ? '' : ''} Rasmlar arxivi
                        </span>
                        <span>{uploadProgress.zip}%</span>
                      </div>
                      <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary">
                        <div
                          className="h-full w-full flex-1 bg-primary transition-all"
                          style={{
                            width: `${uploadProgress.zip}%`,
                            backgroundColor:
                              currentUpload === 'zip' ? '#3b82f6' : '#10b981',
                          }}
                        />
                      </div>
                    </div>

                    {uploadStatus === 'processing' && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Ma'lumotlar qayta ishlanmoqda...</span>
                      </div>
                    )}
                  </div>
                )}

                {uploadStatus === 'error' && (
                  <Alert>
                    <XCircle className="h-4 w-4" />
                    <AlertDescription>
                      Import failed. Please check your file format and try
                      again.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Instructions */}
            <Card>
              <CardHeader>
                <CardTitle>File Format Instructions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <h4>CSV/Excel File Requirements:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• Required columns: Product Name, Category, Price</li>
                    <li>
                      • Optional columns: Description, SKU, Stock, Image
                      Filename
                    </li>
                    <li>• Use UTF-8 encoding</li>
                    <li>• Maximum 1000 products per file</li>
                  </ul>
                </div>

                <div className="space-y-2">
                  <h4>Image ZIP File Requirements:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• Supported formats: JPG, PNG, WebP</li>
                    <li>• Maximum file size: 50MB</li>
                    <li>• Image names should match "Image Filename" column</li>
                    <li>• Recommended size: 800x800px or larger</li>
                  </ul>
                </div>

                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Download the template file to see the exact format required
                    for your data.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </div>

          {/* Upload Results */}
          {uploadResult && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Import Results
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <Badge
                    variant="default"
                    className="bg-green-100 text-green-800"
                  >
                    {uploadResult.success} Successful
                  </Badge>
                  <Badge
                    variant="destructive"
                    className="bg-red-100 text-red-800"
                  >
                    {uploadResult.failed} Failed
                  </Badge>
                  <Badge variant="outline">{uploadResult.total} Total</Badge>
                </div>

                {uploadResult.errors.length > 0 && (
                  <div className="space-y-2">
                    <h4>Errors:</h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Row</TableHead>
                          <TableHead>Field</TableHead>
                          <TableHead>Error</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {uploadResult.errors.map((error, index) => (
                          <TableRow key={index}>
                            <TableCell>{error.row}</TableCell>
                            <TableCell>{error.field}</TableCell>
                            <TableCell className="text-red-600">
                              {error.message}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button onClick={handleBack}>View Products</Button>
                  <Button variant="outline" onClick={resetUpload}>
                    Import More
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Web Scraping Tab */}
        <TabsContent value="web-scraping" className="space-y-6">
          {/* Scraping Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Website Scraping
              </CardTitle>
              <CardDescription>
                Extract product data from e-commerce websites automatically
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Scraping Progress */}
              {scrapingStatus !== 'idle' && (
                <div className="mb-6 p-3 bg-card border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {scrapingStatus === 'scraping' ||
                      scrapingStatus === 'processing' ? (
                        <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
                      ) : scrapingStatus === 'completed' ? (
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                      ) : (
                        <XCircle className="h-3 w-3 text-red-500" />
                      )}
                      <span className="text-sm font-medium">
                        {scrapingStatus === 'scraping'
                          ? 'Scraping...'
                          : scrapingStatus === 'processing'
                            ? 'Processing...'
                            : scrapingStatus === 'completed'
                              ? 'Completed'
                              : scrapingStatus === 'error'
                                ? 'Failed'
                                : 'Idle'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {scrapingStats.processed} / {scrapingStats.total}
                      </span>
                    </div>
                    <span className="text-sm font-medium">
                      {scrapingStats.total > 0
                        ? `${Math.round(
                            (scrapingStats.processed / scrapingStats.total) *
                              100
                          )}%`
                        : '0%'}
                    </span>
                  </div>
                  <Progress value={scrapingProgress} className="h-1.5 mb-1" />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <div>
                      {scrapingStats.speed > 0 && (
                        <span>{scrapingStats.speed} items/sec • </span>
                      )}
                      {['scraping', 'processing'].includes(scrapingStatus) ? (
                        <span>
                          Elapsed:{' '}
                          {formatDuration(scrapingStats.elapsedTime || 0)}
                        </span>
                      ) : scrapingStats.endTime && scrapingStats.startTime ? (
                        <span>
                          Duration:{' '}
                          {formatDuration(
                            Math.round(
                              (scrapingStats.endTime.getTime() -
                                scrapingStats.startTime.getTime()) /
                                1000
                            )
                          )}
                        </span>
                      ) : null}
                    </div>
                    {scrapingStats.startTime && (
                      <span>
                        {['scraping', 'processing'].includes(scrapingStatus)
                          ? `Started ${scrapingStats.startTime.toLocaleTimeString()}`
                          : `Completed at ${(
                              scrapingStats.endTime || new Date()
                            ).toLocaleTimeString()}`}
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Main Website URL</Label>
                <Input
                  placeholder="https://ergo.uz/ofisnye-kresla-i-stulya"
                  value={scrapingUrl}
                  onChange={(e) => setScrapingUrl(e.target.value)}
                  disabled={
                    scrapingStatus === 'scraping' ||
                    scrapingStatus === 'processing'
                  }
                />
              </div>

              {/* Category Selection */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select
                    value={selectedCategory}
                    onValueChange={handleCategoryChange}
                    disabled={
                      scrapingStatus === 'scraping' ||
                      scrapingStatus === 'processing'
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem
                          key={category.id}
                          value={category.id.toString()}
                        >
                          {typeof category.name === 'string'
                            ? category.name
                            : category.name.uz ||
                              category.name.en ||
                              category.name.ru}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Sub Category (Optional)</Label>
                  <Select
                    value={selectedSubCategory}
                    onValueChange={setSelectedSubCategory}
                    disabled={
                      scrapingStatus === 'scraping' ||
                      scrapingStatus === 'processing' ||
                      subCategories.length === 0
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select sub category" />
                    </SelectTrigger>
                    <SelectContent>
                      {subCategories.map((subCategory) => (
                        <SelectItem
                          key={subCategory.id}
                          value={subCategory.id.toString()}
                        >
                          {typeof subCategory.name === 'string'
                            ? subCategory.name
                            : subCategory.name.uz ||
                              subCategory.name.en ||
                              subCategory.name.ru}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Code className="h-4 w-4" />
                    Custom Puppeteer Script
                  </Label>
                  <div className="flex gap-1">
                    <Select onValueChange={loadExampleScript}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="Examples" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="basic">Basic Scraping</SelectItem>
                        <SelectItem value="pagination">
                          With Pagination
                        </SelectItem>
                        <SelectItem value="dynamic">Dynamic Content</SelectItem>
                      </SelectContent>
                    </Select>
                    {puppeteerScript && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyScriptToClipboard}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="relative">
                  <Textarea
                    value={puppeteerScript}
                    onChange={(e) => setPuppeteerScript(e.target.value)}
                    placeholder="// Write your custom Puppeteer script here...
// The script should return an array of product objects
// Available variables: page, url
//
// Example:
// await page.goto(url);
// const products = await page.evaluate(() => {
//   // Your scraping logic here
// });
// return products;"
                    className="min-h-[200px] font-mono text-sm bg-muted/50 border-2 border-dashed resize-y"
                    disabled={
                      scrapingStatus === 'scraping' ||
                      scrapingStatus === 'processing'
                    }
                    style={{
                      fontFamily:
                        'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
                    }}
                  />
                  <Badge
                    variant="secondary"
                    className="absolute top-2 right-2 text-xs bg-background/80"
                  >
                    JavaScript
                  </Badge>
                </div>

                <Alert>
                  <Code className="h-4 w-4" />
                  <AlertDescription>
                    Write JavaScript code using Puppeteer API. Your script
                    should return an array of product objects with properties:
                    name, price, description, image, etc.
                  </AlertDescription>
                </Alert>
              </div>

              <Separator />

              <Button
                onClick={handleWebScraping}
                disabled={
                  !scrapingUrl ||
                  !selectedCategory ||
                  !isConnected ||
                  scrapingStatus === 'scraping' ||
                  scrapingStatus === 'processing'
                }
                className="w-full"
              >
                {scrapingStatus === 'scraping' ||
                scrapingStatus === 'processing' ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {scrapingStatus === 'scraping'
                      ? 'Scraping...'
                      : 'Processing...'}
                  </>
                ) : (
                  <>
                    <Globe className="h-4 w-4 mr-2" />
                    Start Scraping
                  </>
                )}
              </Button>

              {(scrapingStatus === 'scraping' ||
                scrapingStatus === 'processing') && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>
                      {scrapingStatus === 'scraping'
                        ? 'Analyzing website...'
                        : 'Processing data...'}
                    </span>
                    <span>{scrapingProgress}%</span>
                  </div>
                  <Progress value={scrapingProgress} />
                </div>
              )}

              {scrapingStatus === 'error' && (
                <Alert>
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>
                    Scraping failed. Please check the URL and try again.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Preview Products */}
          {previewProducts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Preview Products ({previewProducts.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {previewProducts.map((product, index) => (
                    <Card key={index} className="p-4">
                      <div className="space-y-2">
                        <h4 className="font-medium">{product.name}</h4>
                        <p className="text-sm text-muted-foreground">
                          Price: {product.price}
                        </p>
                        {product.discount && (
                          <p className="text-sm text-green-600">
                            Discount: {product.discount}
                          </p>
                        )}
                        {product.description && (
                          <p className="text-xs text-muted-foreground">
                            {product.description.substring(0, 100)}...
                          </p>
                        )}
                        {product.color && (
                          <p className="text-xs">Color: {product.color}</p>
                        )}
                        {product.image_urls &&
                          product.image_urls.length > 0 && (
                            <div className="flex gap-1">
                              {product.image_urls
                                .slice(0, 3)
                                .map((url, imgIndex) => (
                                  <img
                                    key={imgIndex}
                                    src={url}
                                    alt={`${product.name} ${imgIndex + 1}`}
                                    className="w-8 h-8 object-cover rounded"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                ))}
                            </div>
                          )}
                      </div>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Scraping Results */}
          {scrapingResult && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Scraping Results
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <Badge
                    variant="default"
                    className="bg-green-100 text-green-800"
                  >
                    {scrapingResult.success} Successful
                  </Badge>
                  <Badge
                    variant="destructive"
                    className="bg-red-100 text-red-800"
                  >
                    {scrapingResult.failed} Failed
                  </Badge>
                  <Badge variant="outline">{scrapingResult.total} Total</Badge>
                </div>

                <div className="flex gap-2">
                  <Button onClick={handleBack}>View Products</Button>
                  <Button variant="outline" onClick={resetScraping}>
                    Scrape More
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Scraping Guidelines */}
          <Card>
            <CardHeader>
              <CardTitle>Scraping Guidelines & Tips</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <h4>Category Setup:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Select appropriate category</li>
                    <li>• Choose subcategory if available</li>
                    <li>• Use direct product page URLs</li>
                  </ul>
                </div>

                <div className="space-y-2">
                  <h4>Puppeteer Scripts:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Write custom JavaScript code</li>
                    <li>• Use provided example templates</li>
                    <li>• Access to full Puppeteer API</li>
                  </ul>
                </div>

                <div className="space-y-2">
                  <h4>Supported Sites:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• E-commerce platforms</li>
                    <li>• Shopify, WooCommerce stores</li>
                    <li>• Product catalog websites</li>
                  </ul>
                </div>
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Ensure you have permission to scrape target websites and
                  comply with their terms of service.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
